import XlsxPopulate from 'xlsx-populate/browser/xlsx-populate'
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib'

const formatMoney = (n) => `$${Number(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`
const today = () => new Date().toISOString().split('T')[0]

// =====================================================
// EXCEL: Transacciones de pagos (filtrado)
// =====================================================

export const exportPagosExcel = async (transacciones, { orgName = 'Club', temporada = '' } = {}) => {
  const workbook = await XlsxPopulate.fromBlankAsync()
  const sheet = workbook.sheet(0).name('Pagos')

  // Headers
  const headers = ['Fecha', 'Jugador', 'Monto', 'Metodo', 'Referencia', 'Conceptos', 'Estatus', 'Notas']
  headers.forEach((h, i) => {
    const cell = sheet.cell(1, i + 1)
    cell.value(h)
    cell.style({ bold: true, fill: { type: 'solid', color: '4472C4' }, fontColor: 'FFFFFF', fontSize: 11 })
  })

  // Data
  transacciones.forEach((tx, rowIdx) => {
    const row = rowIdx + 2
    const conceptos = (tx.pagos_transacciones_detalle || [])
      .map((d) => d.pagos_cargos?.pagos_conceptos?.nombre || 'cargo')
      .join(', ')

    sheet.cell(row, 1).value(tx.fecha_pago || '')
    sheet.cell(row, 2).value(tx.jugadores?.nombre_completo || '')
    sheet.cell(row, 3).value(Number(tx.monto || 0)).style({ numberFormat: '$#,##0.00' })
    sheet.cell(row, 4).value(tx.metodo_pago || '')
    sheet.cell(row, 5).value(tx.referencia || '')
    sheet.cell(row, 6).value(conceptos)
    sheet.cell(row, 7).value(tx.anulado ? 'Anulado' : 'Activo')
    sheet.cell(row, 8).value(tx.notas || '')
  })

  // Auto-width approximation
  headers.forEach((_, i) => sheet.column(i + 1).width(18))
  sheet.column(2).width(30)
  sheet.column(6).width(35)

  const blob = await workbook.outputAsync()
  downloadBlob(blob, `pagos_${orgName.replace(/\s/g, '_')}_${today()}.xlsx`)
}

// =====================================================
// EXCEL: Cobranza pendiente (por jugador o por familia)
// =====================================================

export const exportCobranzaExcel = async (cobranzaList, { view = 'jugador', orgName = 'Club', temporada = '' } = {}) => {
  const workbook = await XlsxPopulate.fromBlankAsync()
  const sheet = workbook.sheet(0).name('Cobranza')

  const estatusOf = (saldo, vencido) =>
    Number(vencido || 0) > 0 ? 'Vencido' : (Number(saldo || 0) > 0 ? 'Pendiente' : 'Al corriente')

  const headers = view === 'familia'
    ? ['Familia', 'Jugadores', 'Saldo', 'Vencido', 'Estatus']
    : ['Jugador', 'Numero', 'Familia', 'Saldo', 'Vencido', 'Estatus']

  headers.forEach((h, i) => {
    const cell = sheet.cell(1, i + 1)
    cell.value(h)
    cell.style({ bold: true, fill: { type: 'solid', color: '4472C4' }, fontColor: 'FFFFFF', fontSize: 11 })
  })

  ;(cobranzaList || []).forEach((item, rowIdx) => {
    const row = rowIdx + 2
    const saldo = Number(item.saldo || 0)
    const vencido = Number(item.vencido || 0)
    if (view === 'familia') {
      const jugadores = (item.jugadores || []).map((j) => j.jugador?.nombre_completo).filter(Boolean).join(', ')
      sheet.cell(row, 1).value(item.nombre || '')
      sheet.cell(row, 2).value(jugadores)
      sheet.cell(row, 3).value(saldo).style({ numberFormat: '$#,##0.00' })
      sheet.cell(row, 4).value(vencido).style({ numberFormat: '$#,##0.00' })
      sheet.cell(row, 5).value(estatusOf(saldo, vencido))
    } else {
      sheet.cell(row, 1).value(item.jugador?.nombre_completo || '')
      sheet.cell(row, 2).value(item.jugador?.numero ?? '')
      sheet.cell(row, 3).value(item.familia?.nombre || '')
      sheet.cell(row, 4).value(saldo).style({ numberFormat: '$#,##0.00' })
      sheet.cell(row, 5).value(vencido).style({ numberFormat: '$#,##0.00' })
      sheet.cell(row, 6).value(estatusOf(saldo, vencido))
    }
  })

  headers.forEach((_, i) => sheet.column(i + 1).width(18))
  sheet.column(view === 'familia' ? 2 : 1).width(32)

  const blob = await workbook.outputAsync()
  const suffix = temporada ? `_${String(temporada).replace(/\s/g, '_')}` : ''
  downloadBlob(blob, `cobranza${suffix}_${today()}.xlsx`)
}

// =====================================================
// EXCEL: Estado de cuenta (jugador o familia)
// =====================================================

export const exportEstadoCuentaExcel = async (cargos, { nombre = 'Jugador', orgName = 'Club', tipo = 'jugador' } = {}) => {
  const workbook = await XlsxPopulate.fromBlankAsync()
  const sheet = workbook.sheet(0).name('Estado de Cuenta')

  // Title
  sheet.cell(1, 1).value(`Estado de Cuenta — ${nombre}`).style({ bold: true, fontSize: 14 })
  sheet.cell(2, 1).value(`${orgName} — ${today()}`).style({ fontSize: 10, fontColor: '666666' })

  // Headers
  const headers = tipo === 'familia'
    ? ['Jugador', 'Concepto', 'Monto Original', 'Descuento', 'Monto Final', 'Pagado', 'Saldo', 'Vencimiento', 'Estatus']
    : ['Concepto', 'Monto Original', 'Descuento', 'Monto Final', 'Pagado', 'Saldo', 'Vencimiento', 'Estatus']

  const headerRow = 4
  headers.forEach((h, i) => {
    const cell = sheet.cell(headerRow, i + 1)
    cell.value(h)
    cell.style({ bold: true, fill: { type: 'solid', color: '4472C4' }, fontColor: 'FFFFFF', fontSize: 11 })
  })

  // Data
  cargos.forEach((c, rowIdx) => {
    const row = headerRow + 1 + rowIdx
    let col = 1

    if (tipo === 'familia') {
      sheet.cell(row, col++).value(c.jugadores?.nombre_completo || '')
    }
    sheet.cell(row, col++).value(c.pagos_conceptos?.nombre || '')
    sheet.cell(row, col++).value(Number(c.monto_original || 0)).style({ numberFormat: '$#,##0.00' })
    sheet.cell(row, col++).value(Number(c.descuento_monto || 0)).style({ numberFormat: '$#,##0.00' })
    sheet.cell(row, col++).value(Number(c.monto_final || 0)).style({ numberFormat: '$#,##0.00' })
    sheet.cell(row, col++).value(Number(c.monto_pagado || 0)).style({ numberFormat: '$#,##0.00' })
    sheet.cell(row, col++).value(Number(c.saldo || 0)).style({ numberFormat: '$#,##0.00' })
    sheet.cell(row, col++).value(c.fecha_vencimiento || '')
    sheet.cell(row, col++).value(c.estatus || '')
  })

  // Totals row
  const totalRow = headerRow + 1 + cargos.length + 1
  const totalCol = tipo === 'familia' ? 5 : 4
  sheet.cell(totalRow, totalCol - 1).value('TOTALES').style({ bold: true })
  sheet.cell(totalRow, totalCol).value(cargos.reduce((s, c) => s + Number(c.monto_final || 0), 0)).style({ numberFormat: '$#,##0.00', bold: true })
  sheet.cell(totalRow, totalCol + 1).value(cargos.reduce((s, c) => s + Number(c.monto_pagado || 0), 0)).style({ numberFormat: '$#,##0.00', bold: true })
  sheet.cell(totalRow, totalCol + 2).value(cargos.reduce((s, c) => s + Number(c.saldo || 0), 0)).style({ numberFormat: '$#,##0.00', bold: true })

  // Widths
  headers.forEach((_, i) => sheet.column(i + 1).width(16))
  if (tipo === 'familia') sheet.column(1).width(28)

  const blob = await workbook.outputAsync()
  const filename = `estado_cuenta_${nombre.replace(/\s/g, '_')}_${today()}.xlsx`
  downloadBlob(blob, filename)
}

// =====================================================
// PDF: Dashboard resumen
// =====================================================

export const exportDashboardPdf = async (stats, { orgName = 'Club', temporada = '' } = {}) => {
  const pdfDoc = await PDFDocument.create()
  const page = pdfDoc.addPage([595, 842]) // A4
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica)
  const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold)

  const blue = rgb(0.267, 0.447, 0.769)
  const black = rgb(0, 0, 0)
  const gray = rgb(0.4, 0.4, 0.4)
  const green = rgb(0.2, 0.6, 0.2)
  const red = rgb(0.8, 0.2, 0.2)
  const amber = rgb(0.8, 0.6, 0.1)

  let y = 780

  // Title
  page.drawText(orgName, { x: 50, y, font: fontBold, size: 18, color: blue })
  y -= 22
  page.drawText('Reporte de Cobranza', { x: 50, y, font: fontBold, size: 14, color: black })
  y -= 18
  if (temporada) {
    page.drawText(`Temporada: ${temporada}`, { x: 50, y, font, size: 10, color: gray })
    y -= 14
  }
  page.drawText(`Generado: ${today()}`, { x: 50, y, font, size: 9, color: gray })
  y -= 35

  // Stats cards
  const drawStat = (label, value, color, xPos) => {
    page.drawText(label, { x: xPos, y: y + 14, font, size: 9, color: gray })
    page.drawText(value, { x: xPos, y, font: fontBold, size: 16, color })
  }

  drawStat('Total por cobrar', formatMoney(stats.total_cargos), black, 50)
  drawStat('Cobrado', formatMoney(stats.total_cobrado), green, 200)
  drawStat('Pendiente', formatMoney(stats.total_pendiente), amber, 350)
  y -= 20
  drawStat('Vencido', formatMoney(stats.total_vencido), red, 50)
  y -= 40

  // Progress
  const pct = stats.total_cargos > 0 ? (stats.total_cobrado / stats.total_cargos) : 0
  page.drawText(`Avance de cobranza: ${(pct * 100).toFixed(1)}%`, { x: 50, y, font: fontBold, size: 11, color: black })
  y -= 14

  // Bar
  const barW = 400
  const barH = 12
  page.drawRectangle({ x: 50, y, width: barW, height: barH, color: rgb(0.9, 0.9, 0.9) })
  page.drawRectangle({ x: 50, y, width: barW * Math.min(pct, 1), height: barH, color: green })
  y -= 25

  // Counts
  const counts = [
    `Pagados: ${stats.num_pagados}`,
    `Parciales: ${stats.num_parciales}`,
    `Pendientes: ${stats.num_pendientes}`,
    `Vencidos: ${stats.num_vencidos}`,
    `Total cargos: ${stats.num_cargos}`,
  ]
  counts.forEach((line) => {
    page.drawText(line, { x: 50, y, font, size: 10, color: gray })
    y -= 16
  })

  // Footer
  page.drawText('Generado por MYKLUBB', { x: 50, y: 30, font, size: 8, color: rgb(0.7, 0.7, 0.7) })

  const pdfBytes = await pdfDoc.save()
  downloadBlob(new Blob([pdfBytes], { type: 'application/pdf' }), `reporte_cobranza_${today()}.pdf`)
}

// =====================================================
// Helper: download blob
// =====================================================

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob instanceof Blob ? blob : new Blob([blob]))
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
